import LoadingImport from '../components/LoadingImport'
import ErrorImport from '../components/ErrorImport'

function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: new Promise(resolve => {
      setTimeout(() => {
        resolve(import(`~/pages/${AsyncView}`))
      }, 50)
    }),
    // A component to use while the component is loading.
    loading: LoadingImport,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: ErrorImport,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 200,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 20000,
  })

  return Promise.resolve({
    handler: AsyncHandler(),
    functional: true,
    render(h, { data, children }) {
      return h(AsyncHandler, data, children)
    },
  })
}

export default ({ authGuard, guestGuard }) => [
  // Authenticated routes.
  ...authGuard([{
    path: '/terms_and_conditions_use',
    name: 'terms_and_conditions_use',
    component: () => lazyLoadView('auth/termsAndConditionsUse.vue').then(m => m.default || m),
  }, {
    path: '/2fa',
    name: 'two_step',
    component: () => lazyLoadView('auth/2fa.vue').then(m => m.default || m),
  },
  {
    path: '/home',
    component: () => lazyLoadView('home/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'home.principal',
      },
    },
    {
      path: 'filters',
      name: 'home.principal',
      component: () => lazyLoadView('home/principal/index.vue').then(m => m.default || m),
    },
    {
      path: 'filter_reports',
      name: 'home.filter_reports.index',
      component: () => lazyLoadView('home/filter_reports/index.vue').then(m => m.default || m),
    },
    {
      path: 'filter_reports/create',
      name: 'home.filter_reports.create',
      component: () => lazyLoadView('home/filter_reports/create.vue').then(m => m.default || m),
    },
    {
      path: 'filter_reports/:filter_report_id?',
      name: 'home.filter_reports.show',
      component: () => lazyLoadView('home/filter_reports/show.vue').then(m => m.default || m),
    },
    {
      path: 'reports/:proyecto_id?/:fecha?',
      name: 'home.weekly_blog.index',
      component: () => lazyLoadView('home/report/index.vue').then(m => m.default || m),
    },
    {
      path: 'costumer_report/:proyecto_id?/:fecha?',
      name: 'home.costumer_report.index',
      component: () => lazyLoadView('home/costumer_report/index.vue').then(m => m.default || m),
    },
    {
      path: 'payments/:page?/:tipo_pago_id?',
      name: 'home.pending_payments.index',
      component: () => lazyLoadView('home/payment/index.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/settings',
    component: () => lazyLoadView('settings/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'settings.profile',
      },
    },
    {
      path: 'profile',
      name: 'settings.profile',
      component: () => lazyLoadView('settings/profile.vue').then(m => m.default || m),
    },
    {
      path: 'password',
      name: 'settings.password',
      component: () => lazyLoadView('settings/password.vue').then(m => m.default || m),
    },
    {
      path: 'match',
      name: 'settings.match',
      component: () => lazyLoadView('settings/match.vue').then(m => m.default || m),
    }, {
      path: 'options',
      name: 'settings.options',
      component: () => lazyLoadView('settings/option.vue').then(m => m.default || m),
    }, {
      path: 'security',
      name: 'settings.security',
      component: () => lazyLoadView('settings/security.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/administrations',
    component: () => lazyLoadView('administrations/index.vue').then(m => m.default || m),
    children: [
      {
        path: '',
        redirect: {
          name: 'users.index',
        },
      },
      {
        path: 'roles',
        name: 'roles.index',
        component: () => lazyLoadView('administrations/role/role.vue').then(m => m.default || m),
      },
      {
        path: 'roles/create',
        name: 'roles.create',
        component: () => lazyLoadView('administrations/role/create.vue').then(m => m.default || m),
      },
      {
        path: 'roles/:role_id',
        name: 'roles.show',
        component: () => lazyLoadView('administrations/role/show.vue').then(m => m.default || m),
      },
      {
        path: 'roles/:role_id/edit',
        name: 'roles.edit',
        component: () => lazyLoadView('administrations/role/edit.vue').then(m => m.default || m),
      },
      {
        path: 'users',
        name: 'users.index',
        component: () => lazyLoadView('administrations/user/user.vue').then(m => m.default || m),
      },
      {
        path: 'users/create',
        name: 'users.create',
        component: () => lazyLoadView('administrations/user/create.vue').then(m => m.default || m),
      },
      {
        path: 'users/:user_id',
        name: 'users.show',
        component: () => lazyLoadView('administrations/user/show.vue').then(m => m.default || m),
      },
      {
        path: 'users/:user_id/edit',
        name: 'users.edit',
        component: () => lazyLoadView('administrations/user/edit.vue').then(m => m.default || m),
      },
      {
        path: 'accounting_accounts',
        name: 'accounting_accounts.index',
        component: () => lazyLoadView('administrations/accounting_account/index.vue').then(m => m.default || m),
      },
      {
        path: 'accounting_accounts/create',
        name: 'accounting_accounts.create',
        component: () => lazyLoadView('administrations/accounting_account/create.vue').then(m => m.default || m),
      },
      {
        path: 'accounting_accounts/:accounting_account_id',
        name: 'accounting_accounts.show',
        component: () => lazyLoadView('administrations/accounting_account/show.vue').then(m => m.default || m),
      },
      {
        path: 'accounting_accounts/:accounting_account_id/edit',
        name: 'accounting_accounts.edit',
        component: () => lazyLoadView('administrations/accounting_account/edit.vue').then(m => m.default || m),
      },
      {
        path: 'bank_accounts',
        name: 'bank_accounts.index',
        component: () => lazyLoadView('administrations/bank_account/index.vue').then(m => m.default || m),
      },
      {
        path: 'bank_accounts/create',
        name: 'bank_accounts.create',
        component: () => lazyLoadView('administrations/bank_account/create.vue').then(m => m.default || m),
      },
      {
        path: 'bank_accounts/:bank_account_id/:page?/:desde?/:hasta?',
        name: 'bank_accounts.show',
        component: () => lazyLoadView('administrations/bank_account/show.vue').then(m => m.default || m),
      },
      {
        path: 'bank_accounts/:bank_account_id/edit',
        name: 'bank_accounts.edit',
        component: () => lazyLoadView('administrations/bank_account/edit.vue').then(m => m.default || m),
      },
      {
        path: 'vendors',
        name: 'vendors.index',
        component: () => lazyLoadView('administrations/provider/index.vue').then(m => m.default || m),
      },
      {
        path: 'vendors/create',
        name: 'vendors.create',
        component: () => lazyLoadView('administrations/provider/create.vue').then(m => m.default || m),
      },
      {
        path: 'vendors/:provider_id',
        name: 'vendors.show',
        component: () => lazyLoadView('administrations/provider/show.vue').then(m => m.default || m),
      },
      {
        path: 'vendors/:provider_id/edit',
        name: 'vendors.edit',
        component: () => lazyLoadView('administrations/provider/edit.vue').then(m => m.default || m),
      },
      {
        path: 'lawyers',
        name: 'lawyers.index',
        component: () => lazyLoadView('administrations/lawyer/index.vue').then(m => m.default || m),
      },
      {
        path: 'lawyers/create',
        name: 'lawyers.create',
        component: () => lazyLoadView('administrations/lawyer/create.vue').then(m => m.default || m),
      },
      {
        path: 'lawyers/:lawyer_id',
        name: 'lawyers.show',
        component: () => lazyLoadView('administrations/lawyer/show.vue').then(m => m.default || m),
      },
      {
        path: 'lawyers/:lawyer_id/edit',
        name: 'lawyers.edit',
        component: () => lazyLoadView('administrations/lawyer/edit.vue').then(m => m.default || m),
      },
      {
        path: 'content_templates',
        name: 'content_templates.index',
        component: () => lazyLoadView('administrations/content_template_builder/index.vue').then(m => m.default || m),
      },
      {
        path: 'content_templates/create',
        name: 'content_templates.create',
        component: () => lazyLoadView('administrations/content_template_builder/create.vue').then(m => m.default || m),
      },
      {
        path: 'content_templates/:content_template_id',
        name: 'content_templates.show',
        component: () => lazyLoadView('administrations/content_template_builder/show.vue').then(m => m.default || m),
      },
      {
        path: 'content_templates/:content_template_id/edit',
        name: 'content_templates.edit',
        component: () => lazyLoadView('administrations/content_template_builder/edit.vue').then(m => m.default || m),
      },
    ],
  },
  {
    path: '/all_projects',
    component: () => lazyLoadView('all_projects/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'projects',
      },
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => lazyLoadView('all_projects/project/project.vue').then(m => m.default || m),
    },
    {
      path: '/projects/create',
      name: 'createproject',
      component: () => lazyLoadView('all_projects/project/createproject.vue').then(m => m.default || m),
    },
    {
      path: '/projects/:proyecto_id/edit',
      name: 'editproject',
      component: () => lazyLoadView('all_projects/project/editproject.vue').then(m => m.default || m),
    },
    {
      path: '/projects/:proyecto_id',
      name: 'showproject',
      component: () => lazyLoadView('all_projects/project/showproject.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/all_contracts',
    component: () => lazyLoadView('all_contracts/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'contracts',
      },
    },
    {
      path: '/contracts',
      name: 'contracts',
      component: () => lazyLoadView('all_contracts/contract/contract.vue').then(m => m.default || m),
    },
    {
      path: '/contracts/create',
      name: 'createcontract',
      component: () => lazyLoadView('all_contracts/contract/createcontract.vue').then(m => m.default || m),
    },
    {
      path: '/contracts/:contract_id/edit',
      name: 'editcontrato',
      component: () => lazyLoadView('all_contracts/contract/editcontract.vue').then(m => m.default || m),
    },
    {
      path: '/contracts/:contract_id',
      name: 'showcontract',
      component: () => lazyLoadView('all_contracts/contract/showcontract.vue').then(m => m.default || m),
    },
    {
      path: '/infras_contract',
      name: 'infras_contract.index',
      component: () => lazyLoadView('all_contracts/infra_contract/index.vue').then(m => m.default || m),
    },
    {
      path: '/infras_contract/create',
      name: 'infras_contract.create',
      component: () => lazyLoadView('all_contracts/infra_contract/create.vue').then(m => m.default || m),
    },
    {
      path: '/infras_contract/:contract_id/edit',
      name: 'infras_contract.edit',
      component: () => lazyLoadView('all_contracts/infra_contract/edit.vue').then(m => m.default || m),
    },
    {
      path: '/infras_contract/:contract_id',
      name: 'infras_contract.show',
      component: () => lazyLoadView('all_contracts/infra_contract/show.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/binnacles',
    component: () => lazyLoadView('binnacle/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'diarios',
      },
    },
    {
      path: '/diarios',
      component: () => lazyLoadView('binnacle/daily/diarios.vue').then(m => m.default || m),
      name: 'diarios',
    },
    {
      path: '/diarios/create',
      name: 'diario.create',
      component: () => lazyLoadView('binnacle/daily/createddiario.vue').then(m => m.default || m),
    },
    {
      path: '/diarios/:diario_id/edit',
      name: 'diario.edit',
      component: () => lazyLoadView('binnacle/daily/editdiario.vue').then(m => m.default || m),
    },
    {
      path: '/diarios/:project_id/:created_at',
      name: 'diario.show',
      component: () => lazyLoadView('binnacle/daily/showdiario.vue').then(m => m.default || m),
      children: [
        {
          path: '/diarios/:project_id/:type_contract_id/:created_at/:contract_id',
          name: 'diario.show.contract',
          component: () => lazyLoadView('binnacle/daily/dailyContractDetail.vue').then(m => m.default || m),
        },
      ],
    },
    {
      path: '/programming',
      name: 'programming.index',
      component: () => lazyLoadView('binnacle/programming/index.vue').then(m => m.default || m),
    },
    {
      path: '/programming/create',
      name: 'programming.create',
      component: () => lazyLoadView('binnacle/programming/create.vue').then(m => m.default || m),
    },
    {
      path: '/programming/:programming_id',
      name: 'programming.show',
      component: () => lazyLoadView('binnacle/programming/show.vue').then(m => m.default || m),
    },
    {
      path: '/programming/:programming_id/edit',
      name: 'programming.edit',
      component: () => lazyLoadView('binnacle/programming/edit.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/requests',
    component: () => lazyLoadView('requests/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'materials',
      },
    },
    {
      path: '/materials',
      name: 'materials',
      component: () => lazyLoadView('requests/material/material.vue').then(m => m.default || m),
    },
    {
      path: '/materials/create',
      name: 'creatematerial',
      component: () => lazyLoadView('requests/material/creatematerial.vue').then(m => m.default || m),
    },
    {
      path: '/materials/:material_id/edit',
      name: 'editmaterial',
      component: () => lazyLoadView('requests/material/editmaterial.vue').then(m => m.default || m),
    },
    {
      path: '/materials/:material_id',
      name: 'showmaterial',
      component: () => lazyLoadView('requests/material/showmaterial.vue').then(m => m.default || m),
    },
    {
      path: '/materials/:material_id/coincidences',
      name: 'coincidencesMaterial',
      component: () => lazyLoadView('requests/material/coincidencesMaterial.vue').then(m => m.default || m),
    },
    {
      path: '/extras',
      component: () => lazyLoadView('requests/extra/extras.vue').then(m => m.default || m),
      name: 'extras',
    },
    {
      path: '/extras/create',
      name: 'extra.create',
      component: () => lazyLoadView('requests/extra/createextra.vue').then(m => m.default || m),
    },
    {
      path: '/extras/:extra_id',
      name: 'extra.show',
      component: () => lazyLoadView('requests/extra/showextra.vue').then(m => m.default || m),
    },
    {
      path: '/extras/:extra_id/edit',
      name: 'extra.edit',
      component: () => lazyLoadView('requests/extra/editextra.vue').then(m => m.default || m),
    },
    {
      path: '/extras/:extra_id/coincidences',
      name: 'coincidencesExtra',
      component: () => lazyLoadView('requests/extra/coincidencesExtra.vue').then(m => m.default || m),
    },
    {
      path: '/garantias',
      component: () => lazyLoadView('requests/garantia/garantias.vue').then(m => m.default || m),
      name: 'garantias',
    },
    {
      path: '/garantias/create',
      name: 'garantias.create',
      component: () => lazyLoadView('requests/garantia/create.vue').then(m => m.default || m),
    },
    {
      path: '/garantias/:garantia_id',
      name: 'garantias.show',
      component: () => lazyLoadView('requests/garantia/show.vue').then(m => m.default || m),
    },
    {
      path: '/garantias/:garantia_id/edit',
      name: 'garantias.edit',
      component: () => lazyLoadView('requests/garantia/edit.vue').then(m => m.default || m),
    },
    {
      path: '/garantias/:garantia_id/coincidences',
      name: 'coincidencesGarantia',
      component: () => lazyLoadView('requests/garantia/coincidencesGarantia.vue').then(m => m.default || m),
    },
    {
      path: '/payments',
      name: 'payments',
      component: () => lazyLoadView('requests/pago/index.vue').then(m => m.default || m),
    },
    {
      path: '/payments/create',
      name: 'createpayment',
      component: () => lazyLoadView('requests/pago/create.vue').then(m => m.default || m),
    },
    {
      path: '/payments/:payment_id/edit',
      name: 'editpayment',
      component: () => lazyLoadView('requests/pago/edit.vue').then(m => m.default || m),
    },
    {
      path: '/payments/:payment_id',
      name: 'showpayment',
      component: () => lazyLoadView('requests/pago/show.vue').then(m => m.default || m),
    },
    {
      path: '/payments/:payment_id/coincidences',
      name: 'coincidencesPayment',
      component: () => lazyLoadView('requests/pago/coincidencesPago.vue').then(m => m.default || m),
    },
    {
      path: '/tiny_box',
      name: 'tiny_box.index',
      component: () => lazyLoadView('requests/caja_menuda/index.vue').then(m => m.default || m),
    },
    {
      path: '/tiny_box/create',
      name: 'tiny_box.create',
      component: () => lazyLoadView('requests/caja_menuda/create.vue').then(m => m.default || m),
    },
    {
      path: '/tiny_box/:tiny_box_id/edit',
      name: 'tiny_box.edit',
      component: () => lazyLoadView('requests/caja_menuda/edit.vue').then(m => m.default || m),
    },
    {
      path: '/tiny_box/:tiny_box_id',
      name: 'tiny_box.show',
      component: () => lazyLoadView('requests/caja_menuda/show.vue').then(m => m.default || m),
    },
    {
      path: '/tiny_box/:tiny_box_id/coincidences',
      name: 'coincidencesTinyBox',
      component: () => lazyLoadView('requests/caja_menuda/coincidencesCajaMenuda.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/permissions',
    component: () => lazyLoadView('permissions/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'blueprints',
      },
    }, {
      path: 'blueprints/:page?/:proyecto_id?/:tipo_plano?',
      name: 'blueprints',
      component: () => lazyLoadView('permissions/flat/index.vue').then(m => m.default || m),
    },
    {
      path: 'policies/:page?/:proyecto_id?',
      name: 'policies',
      component: () => lazyLoadView('permissions/policy/index.vue').then(m => m.default || m),
    },
    {
      path: 'construction_permit/:page?/:proyecto_id?/:tipo_permiso?',
      name: 'construction_permit',
      component: () => lazyLoadView('permissions/construction_permit/index.vue').then(m => m.default || m),
    },
    {
      path: 'occupation_permit/:page?/:proyecto_id?/:tipo_permiso?',
      name: 'occupation_permit',
      component: () => lazyLoadView('permissions/occupation_permit/index.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/sales',
    component: () => lazyLoadView('sales/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'precontracts.index',
      },
    },
    {
      path: 'precontracts',
      name: 'precontracts.index',
      component: () => lazyLoadView('sales/precontract/index.vue').then(m => m.default || m),
    },
    {
      path: 'precontracts/:precontract_id',
      name: 'precontracts.show',
      component: () => lazyLoadView('sales/precontract/show.vue').then(m => m.default || m),
    },
    {
      path: 'precontracts/:precontract_id/edit',
      name: 'precontracts.edit',
      component: () => lazyLoadView('sales/precontract/edit.vue').then(m => m.default || m),
    },
    {
      path: 'precontracts/create_appointment/:precontract_id/:project_id',
      name: 'precontracts.create_appointment',
      component: () => lazyLoadView('sales/precontract/createAppointment.vue').then(m => m.default || m),
    },
    {
      path: 'precontracts/reschedule_appointment/:precontract_id/:appointment_id/:project_id',
      name: 'precontracts.reschedule_appointment',
      component: () => lazyLoadView('sales/precontract/rescheduleAppointment.vue').then(m => m.default || m),
    },
    {
      path: 'precontracts/:precontract_id/active',
      name: 'precontracts.active_files',
      component: () => lazyLoadView('sales/precontract/libraryActive.vue').then(m => m.default || m),
    },
    {
      path: 'models',
      name: 'models.index',
      component: () => lazyLoadView('sales/models/index.vue').then(m => m.default || m),
    },
    {
      path: 'models/create',
      name: 'models.create',
      component: () => lazyLoadView('sales/models/create.vue').then(m => m.default || m),
    },
    {
      path: 'models/:model_id',
      name: 'models.show',
      component: () => lazyLoadView('sales/models/show.vue').then(m => m.default || m),
    },
    {
      path: 'models/:model_id/edit',
      name: 'models.edit',
      component: () => lazyLoadView('sales/models/edit.vue').then(m => m.default || m),
    },
    {
      path: 'models/:model_id/select_main_image',
      name: 'models.select_main_image',
      component: () => lazyLoadView('sales/models/selectMainImage.vue').then(m => m.default || m),
    },
    {
      path: 'appointments',
      name: 'appointments.index',
      component: () => lazyLoadView('sales/appointments/index.vue').then(m => m.default || m),
    },
    {
      path: 'appointments/reschedule/:appointment_id/:project_id',
      name: 'appointments.reschedule',
      component: () => lazyLoadView('sales/appointments/reschedule.vue').then(m => m.default || m),
    },
    {
      path: 'appointments/project_settings',
      name: 'appointments.project_settings',
      component: () => lazyLoadView('sales/appointments/project_settings/projects.vue').then(m => m.default || m),
    },
    {
      path: 'appointments/project_settings/:project_id',
      name: 'appointments.project_settings.appointment_days',
      component: () => lazyLoadView('sales/appointments/project_settings/appointment_days.vue').then(m => m.default || m),
    },
    {
      path: 'appointments/project_settings/:project_id/create_appointment_day',
      name: 'appointments.project_settings.create_appointment_day',
      component: () => lazyLoadView('sales/appointments/project_settings/create_appointment_day.vue').then(m => m.default || m),
    },
    {
      path: 'appointments/project_settings/:appointment_day_id/:project_id/edit_appointment_day',
      name: 'appointments.project_settings.edit_appointment_day',
      component: () => lazyLoadView('sales/appointments/project_settings/edit_appointment_day.vue').then(m => m.default || m),
    },
    {
      path: 'conversations',
      name: 'conversations.index',
      component: () => lazyLoadView('sales/conversation/index.vue').then(m => m.default || m),
    },
    {
      path: 'conversations/:phone',
      name: 'conversations.show',
      component: () => lazyLoadView('sales/conversation/show.vue').then(m => m.default || m),
    },
    {
      path: 'messages/:phone?',
      name: 'sale.messages.index',
      component: () => lazyLoadView('sales/messages/index.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/formalities',
    component: () => lazyLoadView('formalities/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'formalities.index',
      },
    },
    {
      path: 'procedure',
      name: 'formalities.index',
      component: () => lazyLoadView('formalities/procedure/index.vue').then(m => m.default || m),
    },
    {
      path: '/formalities/:precontract_id',
      name: 'formalities.show',
      component: () => lazyLoadView('formalities/procedure/show.vue').then(m => m.default || m),
    },
    {
      path: '/formalities/:precontract_id/edit',
      name: 'formalities.edit',
      component: () => lazyLoadView('formalities/procedure/edit.vue').then(m => m.default || m),
    },
    {
      path: 'formalities/create_appointment/:precontract_id/:project_id',
      name: 'formalities.create_appointment',
      component: () => lazyLoadView('formalities/procedure/createAppointment.vue').then(m => m.default || m),
    },
    {
      path: 'formalities/reschedule_appointment/:precontract_id/:appointment_id/:project_id',
      name: 'formalities.reschedule_appointment',
      component: () => lazyLoadView('formalities/procedure/rescheduleAppointment.vue').then(m => m.default || m),
    }],
  },
  {
    path: '/tickets',
    name: 'tickets.index',
    component: () => lazyLoadView('tickets/index.vue').then(m => m.default || m),
  },
  {
    path: '/tickets/:ticket_id',
    name: 'tickets.show',
    component: () => lazyLoadView('tickets/show.vue').then(m => m.default || m),
  },
  {
    path: '/costumer_payments',
    component: () => lazyLoadView('costumer_payments/index.vue').then(m => m.default || m),
    children: [{
      path: '',
      redirect: {
        name: 'costumer_payments.index',
      },
    },
    {
      path: 'payments',
      name: 'costumer_payments.index',
      component: () => lazyLoadView('costumer_payments/payments/index.vue').then(m => m.default || m),
    },
    {
      path: '/costumer_payments/:precontract_id',
      name: 'costumer_payments.show',
      component: () => lazyLoadView('costumer_payments/payments/show.vue').then(m => m.default || m),
    },
    {
      path: '/costumer_payments/:payment_id/detail',
      name: 'costumer_payments.detail_payment',
      component: () => lazyLoadView('costumer_payments/payments/detailPayment.vue').then(m => m.default || m),
    }],
  }]),

  // Guest routes.
  ...guestGuard([{
    path: '/login',
    name: 'login',
    component: () => lazyLoadView('auth/login.vue').then(m => m.default || m),
  },
  {
    path: '/atencion',
    name: 'atencion',
    component: () => lazyLoadView('requests/garantia/costumer/create.vue').then(m => m.default || m),
  },
  {
    path: '/solicitud_creada',
    name: 'solicitud_creada',
    component: () => lazyLoadView('requests/garantia/costumer/created.vue').then(m => m.default || m),
  },
  {
    path: '/verificar_atencion/:codigo',
    name: 'verificar_atencion',
    component: () => lazyLoadView('requests/garantia/costumer/verify.vue').then(m => m.default || m),
  },
  {
    path: '/solicitud_aprobada',
    name: 'solicitud_aprobada',
    component: () => lazyLoadView('requests/garantia/costumer/approved.vue').then(m => m.default || m),
  },
  {
    path: '/solicitud_rechazada',
    name: 'solicitud_rechazada',
    component: () => lazyLoadView('requests/garantia/costumer/rejected.vue').then(m => m.default || m),
  },
  {
    path: '/solicitud_no_encontrada',
    name: 'solicitud_no_encontrada',
    component: () => lazyLoadView('requests/garantia/costumer/notFound.vue').then(m => m.default || m),
  },
  {
    path: '/agendar_cita',
    name: 'agendar_cita.enter_cedula',
    component: () => lazyLoadView('sales/appointments/guest/enterCedula.vue').then(m => m.default || m),
  },
  {
    path: '/agendar_cita/seleccionar_fecha/:appointment_code',
    name: 'agendar_cita.seleccionar_fecha',
    component: () => lazyLoadView('sales/appointments/guest/selectDateSchedule.vue').then(m => m.default || m),
  },
  {
    path: '/agendar_cita/seleccionar_hora/:appointment_code',
    name: 'agendar_cita.seleccionar_hora',
    component: () => lazyLoadView('sales/appointments/guest/selectTime.vue').then(m => m.default || m),
  },
  {
    path: '/agendar_cita/confirmar_cita/:appointment_code',
    name: 'agendar_cita.confirmar_cita',
    component: () => lazyLoadView('sales/appointments/guest/confirmAppointment.vue').then(m => m.default || m),
  },
  {
    path: '/cancelar_cita/:code',
    name: 'cancelar_cita',
    component: () => lazyLoadView('sales/appointments/guest/cancel.vue').then(m => m.default || m),
  },
  {
    path: '/cita_reservada/:appointment_code',
    name: 'cita_reservada',
    component: () => lazyLoadView('sales/appointments/guest/created.vue').then(m => m.default || m),
  },
  {
    path: '/actualizar_requerimientos',
    name: 'actualizar_requerimientos.enter_cedula',
    component: () => lazyLoadView('sales/requirement/check.vue').then(m => m.default || m),
  }]),
  {
    alias: '*',
    path: '/not_found',
    name: 'errors.404',
    component: () => lazyLoadView('errors/404.vue').then(m => m.default || m),
  },
]
